import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Input, Spin, Tooltip, Button, Badge, Dropdown, Menu } from "antd";
import { SearchOutlined, PlusOutlined, EditOutlined, LeftOutlined, RightOutlined, FilterOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import AddUsers from "../../../pages/AddUsers";
import UserService from "../../../utils/api/users";
import "../UsersList/userStyle.css";

export default function UsersList(props) {
  const location = useLocation();
  const role = localStorage.getItem("role");
  const canManageUsers = String(role || "").trim() === "1";
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [roleFilter, setRoleFilter] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState("");

  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const tableContainerRef = useRef(null);

  const roleMapping = useMemo(() => ({
    "1": "ADMIN",
    "2": "FINANZAS",
    "3": "COMPRAS",
    "4": "CLIENTE",
    "5": "VENTAS",
    "6": "PROVEEDOR",
    "9": "REPARTIDOR"
  }), []);

  const reverseRoleMapping = useMemo(() => {
    return Object.entries(roleMapping).reduce((acc, [key, value]) => {
      acc[value] = key;
      return acc;
    }, {});
  }, [roleMapping]);

  const roleOptions = useMemo(() => [
    { value: null, label: "Todos los tipos" },
    { value: "ADMIN", label: "ADMIN", color: "#d9d9d9" },
    { value: "FINANZAS", label: "FINANZAS", color: "#d3adf7" },
    { value: "COMPRAS", label: "COMPRAS", color: "#91d5ff" },
    { value: "CLIENTE", label: "CLIENTE", color: "#ffd591" },
    { value: "VENTAS", label: "VENTAS", color: "#b7eb8f" },
    { value: "PROVEEDOR", label: "PROVEEDOR", color: "#87e8de" },
    { value: "REPARTIDOR", label: "REPARTIDOR", color: "#91d5ff" }
  ], []);

  const scrollToTop = useCallback(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);

  const applyFilters = useCallback((users, search, status, role) => {
    let result = [...users];

    if (search && search.trim() !== "") {
      const searchLower = search.toLowerCase().trim();
      result = result.filter(user =>
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        (user.phone && user.phone.toLowerCase().includes(searchLower))
      );
    }

    if (status) {
      if (status === 'ACTIVO') {
        result = result.filter(user => user.status === true || user.status === "true" || user.status === 1);
      } else if (status === 'INACTIVO') {
        result = result.filter(user => user.status === false || user.status === "false" || user.status === 0);
      }
    }

    if (role) {
      const roleNumber = reverseRoleMapping[role];
      if (roleNumber) {
        result = result.filter(user => user.role?.toString() === roleNumber);
      }
    }

    return result;
  }, [reverseRoleMapping]);

  const getNextCursorFromResponse = (responseData) => {
    return responseData?.nextCursor || responseData?.nextPage || null;
  };

  const fetchAllUsers = useCallback(async () => {
    if (loading) return;

    setLoading(true);
    try {
      let cursor = null;
      let attempts = 0;
      let collectedUsers = [];
      const batchLimit = 50;

      while (attempts < 100) {
        const response = await UserService.GetAll({
          limit: batchLimit,
          lastVisible: cursor
        });

        if (!(response.data && response.data.success)) {
          break;
        }

        const batchUsers = response.data.users || [];
        const nextCursor = getNextCursorFromResponse(response.data);

        if (batchUsers.length > 0) {
          collectedUsers = [...collectedUsers, ...batchUsers];
        }

        if (!nextCursor || batchUsers.length === 0 || nextCursor === cursor) {
          break;
        }

        cursor = nextCursor;
        attempts += 1;
      }

      const usersMap = new Map();
      collectedUsers.forEach((user) => {
        if (user?.id) {
          usersMap.set(user.id, user);
        }
      });

      const allUsersList = Array.from(usersMap.values());
      setAllUsers(allUsersList);
    } catch (error) {
      setAllUsers([]);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    fetchAllUsers();
  }, [props.reload, fetchAllUsers]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const incomingSearch = searchParams.get('search');
    const incomingRole = searchParams.get('role');

    const normalizedSearch = incomingSearch && incomingSearch.trim() !== ''
      ? incomingSearch.trim()
      : null;

    const normalizedRole = incomingRole && incomingRole.trim() !== ''
      ? incomingRole.trim().toUpperCase()
      : null;

    const isValidRoleFilter = normalizedRole && roleOptions.some(
      (option) => option.value === normalizedRole
    );

    const effectiveRoleFilter = isValidRoleFilter ? normalizedRole : null;

    const isSameSearchState = normalizedSearch
      ? (isSearching && searchTerm === normalizedSearch)
      : (!isSearching && searchTerm === '');

    const isSameRoleState = roleFilter === effectiveRoleFilter;

    if (isSameSearchState && isSameRoleState) {
      return;
    }

    setRoleFilter(effectiveRoleFilter);

    if (normalizedSearch) {
      setSearchInputValue(normalizedSearch);
      setSearchTerm(normalizedSearch);
      setIsSearching(true);
      setCurrentPage(1);
      return;
    }

    setSearchInputValue("");
    setSearchTerm("");
    setIsSearching(false);
    setCurrentPage(1);
  }, [location.search, roleOptions]);

  useEffect(() => {
    const filtered = applyFilters(allUsers, isSearching ? searchTerm : null, statusFilter, roleFilter);
    setFilteredUsers(filtered);
  }, [allUsers, applyFilters, isSearching, roleFilter, searchTerm, statusFilter]);

  useEffect(() => {
    const nextTotalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
    if (currentPage > nextTotalPages) {
      setCurrentPage(nextTotalPages);
    }
  }, [filteredUsers.length, pageSize, currentPage]);

  const handleStatusFilter = useCallback((status) => {
    setStatusFilter(status);
    setCurrentPage(1);

    const filtered = applyFilters(allUsers, isSearching ? searchTerm : null, status, roleFilter);
    setFilteredUsers(filtered);
  }, [allUsers, isSearching, searchTerm, roleFilter, applyFilters]);

  const handleRoleFilter = useCallback((role) => {
    setRoleFilter(role);
    setCurrentPage(1);

    const filtered = applyFilters(allUsers, isSearching ? searchTerm : null, statusFilter, role);
    setFilteredUsers(filtered);
  }, [allUsers, isSearching, searchTerm, statusFilter, applyFilters]);

  const handleClearStatusFilter = useCallback(() => {
    setStatusFilter(null);
    const filtered = applyFilters(allUsers, isSearching ? searchTerm : null, null, roleFilter);
    setFilteredUsers(filtered);
  }, [allUsers, isSearching, searchTerm, roleFilter, applyFilters]);

  const handleClearRoleFilter = useCallback(() => {
    setRoleFilter(null);
    const filtered = applyFilters(allUsers, isSearching ? searchTerm : null, statusFilter, null);
    setFilteredUsers(filtered);
  }, [allUsers, isSearching, searchTerm, statusFilter, applyFilters]);

  const handleClearSearch = useCallback(() => {
    setSearchInputValue("");
    setSearchTerm("");
    setIsSearching(false);
    setCurrentPage(1);
  }, []);

  const handleSearch = useCallback(() => {
    const trimmedSearch = searchInputValue.trim();

    if (trimmedSearch === "") {
      handleClearSearch();
      return;
    }

    setSearchTerm(trimmedSearch);
    setIsSearching(true);
  }, [searchInputValue, handleClearSearch]);

  const handleClearAllFilters = useCallback(() => {
    setSearchInputValue("");
    setSearchTerm("");
    setStatusFilter(null);
    setRoleFilter(null);
    setIsSearching(false);
    setCurrentPage(1);

    const filtered = applyFilters(allUsers, null, null, null);
    setFilteredUsers(filtered);
  }, [allUsers, applyFilters]);

  const handleNextPage = () => {
    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
      scrollToTop();
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      scrollToTop();
    }
  };

  const getDisplayRange = () => {
    if (filteredUsers.length === 0) {
      return { start: 0, end: 0 };
    }
    const start = ((currentPage - 1) * pageSize) + 1;
    const end = Math.min(currentPage * pageSize, filteredUsers.length);
    return { start, end };
  };

  const handlePageSizeChange = async (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    scrollToTop();
  };

  const handleAddUser = useCallback(() => setShowAddUserModal(true), []);

  const handleEditUser = useCallback((user) => {
    setEditingUser(user.userData);
    setShowEditUserModal(true);
  }, []);

  const handleModalCancel = useCallback(() => {
    setShowAddUserModal(false);
    setShowEditUserModal(false);
    setEditingUser(null);
  }, []);

  const handleUserSuccess = useCallback(() => {
    setShowAddUserModal(false);
    setShowEditUserModal(false);
    setEditingUser(null);

    fetchAllUsers();
  }, [fetchAllUsers]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    setSearchTerm(value);
    setIsSearching(value.trim() !== "");
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const getStatusBadgeClass = (status) => {
    if (status === "ACTIVO") return "status-badge activo";
    if (status === "INACTIVO") return "status-badge inactivo";
    return "status-badge desconocido";
  };

  const getTypeBadgeClass = (type) => {
    const typeMap = {
      "ADMIN": "type-badge administrador",
      "FINANZAS": "type-badge finanzas",
      "COMPRAS": "type-badge comprador",
      "CLIENTE": "type-badge cliente",
      "VENTAS": "type-badge vendedor",
      "PROVEEDOR": "type-badge proveedor",
      "REPARTIDOR": "type-badge repartidor"
    };
    return typeMap[type] || "type-badge";
  };

  const statusMenu = (
    <Menu>
      <Menu.Item
        key="all-status"
        onClick={() => handleStatusFilter(null)}
        style={statusFilter === null ? { backgroundColor: '#e6f7ff' } : {}}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>Todos los estados</span>
          {statusFilter === null && <span style={{ marginLeft: 'auto' }}>✓</span>}
        </div>
      </Menu.Item>
      <Menu.Item
        key="active"
        onClick={() => handleStatusFilter('ACTIVO')}
        style={statusFilter === 'ACTIVO' ? { backgroundColor: '#e6f7ff' } : {}}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Badge status="success" />
          <span>Activos</span>
          {statusFilter === 'ACTIVO' && <span style={{ marginLeft: 'auto' }}>✓</span>}
        </div>
      </Menu.Item>
      <Menu.Item
        key="inactive"
        onClick={() => handleStatusFilter('INACTIVO')}
        style={statusFilter === 'INACTIVO' ? { backgroundColor: '#e6f7ff' } : {}}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Badge status="error" />
          <span>Inactivos</span>
          {statusFilter === 'INACTIVO' && <span style={{ marginLeft: 'auto' }}>✓</span>}
        </div>
      </Menu.Item>
    </Menu>
  );

  const roleMenu = (
    <Menu>
      {roleOptions.map(option => (
        <Menu.Item
          key={option.value || "all-roles"}
          onClick={() => handleRoleFilter(option.value)}
          style={roleFilter === option.value ? { backgroundColor: '#e6f7ff' } : {}}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {option.value ? (
              <>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: option.color || '#d9d9d9',
                    flexShrink: 0
                  }}
                />
                <span style={{ fontWeight: 500 }}>{option.label}</span>
              </>
            ) : (
              <span>{option.label}</span>
            )}
            {roleFilter === option.value && <span style={{ marginLeft: 'auto' }}>✓</span>}
          </div>
        </Menu.Item>
      ))}
    </Menu>
  );

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredUsers.slice(start, end);
  }, [filteredUsers, currentPage, pageSize]);

  const data = useMemo(() => {
    return paginatedUsers.map((user) => {
      const userStatus = user.status;
      let statusText = "DESCONOCIDO";

      if (userStatus === true || userStatus === "true" || userStatus === 1) {
        statusText = "ACTIVO";
      } else if (userStatus === false || userStatus === "false" || userStatus === 0) {
        statusText = "INACTIVO";
      } else if (userStatus === undefined || userStatus === null) {
        statusText = "ACTIVO";
      }

      const userRole = user.role?.toString();
      const roleName = roleMapping[userRole] || `Rol ${userRole}`;

      return {
        key: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone || "No disponible",
        type: roleName,
        roleNumber: userRole,
        status: statusText,
        statusBool: user.status,
        userData: user
      };
    });
  }, [paginatedUsers, roleMapping]);

  const displayRange = getDisplayRange();
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));

  const hasActiveFilters = isSearching || statusFilter !== null || roleFilter !== null;

  if (loading && allUsers.length === 0) {
    return (
      <div className="admin-users-container">
        <div className="orders-card">
          <div className="loading-container">
            <Spin size="large" />
            <p>Cargando usuarios...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-users-container">
      <div className="orders-card">
        <div className="table-header-section">
          <h1>Lista de Usuarios</h1>

          <div className="filters-section" style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div className="search-filter" style={{ flex: 1 }}>
              <Input
                placeholder="Buscar usuarios por nombre, email o teléfono..."
                value={searchInputValue}
                onChange={handleSearchChange}
                onPressEnter={handleSearchKeyPress}
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                allowClear
                size="large"
                style={{ 
                  width: '100%',
                  borderRadius: '8px',
                  border: '1px solid #d9d9d9'
                }}
              />
            </div>
            <div className="add-user-filter">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddUser}
                disabled={!canManageUsers}
                size="large"
                style={{
                  backgroundColor: '#0f5197',
                  borderColor: '#0f5197',
                  borderRadius: '8px',
                  height: '40px',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  boxShadow: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#002140';
                  e.currentTarget.style.borderColor = '#002140';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#0f5197';
                  e.currentTarget.style.borderColor = '#0f5197';
                }}
              >
                Agregar usuario
              </Button>
            </div>
          </div>
        </div>

        {hasActiveFilters && (
          <div className="active-filters-section" style={{
            marginBottom: '16px',
            padding: '12px 16px',
            background: '#f0f8ff',
            borderRadius: '8px',
            border: '1px solid #91d5ff',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontWeight: '500', color: '#0f5197', whiteSpace: 'nowrap' }}>
              Filtros activos:
            </span>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', flex: 1 }}>
              {isSearching && (
                <span style={{
                  background: '#e6f7ff',
                  color: '#0f5197',
                  border: '1px solid #91d5ff',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '12px'
                }}>
                  Búsqueda: "{searchTerm}"
                </span>
              )}
              {statusFilter && (
                <span
                  style={{
                    background: statusFilter === 'ACTIVO' ? '#f6ffed' : '#fff2f0',
                    color: statusFilter === 'ACTIVO' ? '#52c41a' : '#ff4d4f',
                    border: statusFilter === 'ACTIVO' ? '1px solid #b7eb8f' : '1px solid #ffccc7',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '12px'
                  }}
                >
                  {statusFilter === 'ACTIVO' ? '✅ Activo' : '❌ Inactivo'}
                </span>
              )}
              {roleFilter && (
                <span
                  style={{
                    background: '#f9f0ff',
                    color: '#722ed1',
                    border: '1px solid #d3adf7',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '12px'
                  }}
                >
                  Tipo: {roleFilter}
                </span>
              )}
            </div>
            <Button
              type="link"
              size="small"
              onClick={handleClearAllFilters}
              style={{ marginLeft: 'auto', whiteSpace: 'nowrap', color: '#0f5197' }}
            >
              Restablecer Filtros
            </Button>
          </div>
        )}

        <div className="table-container" ref={tableContainerRef}>
          <div className="styled-table custom-table">
            <div className="table-header">
              <div className="header-row">
                <div className="header-cell nombre">Nombre</div>
                <div className="header-cell email">Email</div>
                <div className="header-cell telefono">Teléfono</div>
                <div className="header-cell tipo">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <Dropdown
                      overlay={roleMenu}
                      trigger={['click']}
                      placement="bottomRight"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Tipo
                        <FilterOutlined style={{
                          fontSize: '12px',
                          color: roleFilter !== null ? '#1890ff' : 'rgba(255, 255, 255, 0.85)',
                          marginLeft: '4px'
                        }} />
                        {roleFilter !== null && (
                          <Badge
                            dot
                            style={{
                              backgroundColor: '#722ed1',
                              marginLeft: '4px'
                            }}
                          />
                        )}
                      </div>
                    </Dropdown>
                  </div>
                </div>
                <div className="header-cell estatus">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <Dropdown
                      overlay={statusMenu}
                      trigger={['click']}
                      placement="bottomRight"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Estatus
                        <FilterOutlined style={{
                          fontSize: '12px',
                          color: statusFilter !== null ? '#1890ff' : 'rgba(255, 255, 255, 0.85)',
                          marginLeft: '4px'
                        }} />
                        {statusFilter !== null && (
                          <Badge
                            dot
                            style={{
                              backgroundColor: '#1890ff',
                              marginLeft: '4px'
                            }}
                          />
                        )}
                      </div>
                    </Dropdown>
                  </div>
                </div>
                <div className="header-cell accion">Acciones</div>
              </div>
            </div>

            <div className="table-body">
              {filteredUsers.length === 0 ? (
                <div className="empty-state">
                  {loading ? (
                    <Spin size="small" />
                  ) : hasActiveFilters ? (
                    <div>
                      <p>No se encontraron usuarios con los filtros aplicados</p>
                      <Button
                        type="link"
                        onClick={handleClearAllFilters}
                        style={{ marginTop: '8px', color: '#0f5197' }}
                      >
                        Mostrar todos los usuarios
                      </Button>
                    </div>
                  ) : (
                    <p>No hay usuarios disponibles</p>
                  )}
                </div>
              ) : (
                data.map(user => (
                  <div key={user.key} className="table-row">
                    <div className="body-row">
                      <div className="body-cell nombre" data-label="Nombre">{user.name}</div>
                      <div className="body-cell email" data-label="Email">{user.email}</div>
                      <div className="body-cell telefono" data-label="Teléfono">{user.phone}</div>
                      <div className="body-cell tipo" data-label="Tipo">
                        <span className={getTypeBadgeClass(user.type)}>
                          {user.type}
                        </span>
                      </div>
                      <div className="body-cell estatus" data-label="Estatus">
                        <span className={getStatusBadgeClass(user.status)}>
                          {user.status}
                        </span>
                      </div>
                      <div className="body-cell accion" data-label="Acciones">
                        <Tooltip title="Editar usuario" placement="left">
                          <button
                            className="edit-icon-button"
                            onClick={() => handleEditUser(user)}
                            disabled={!canManageUsers}
                          >
                            <EditOutlined />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {filteredUsers.length > 0 && (
          <div className="main-pagination-container">
            <div className="pagination-info">
              <span>
                {hasActiveFilters
                  ? `Mostrando ${filteredUsers.length} usuario${filteredUsers.length !== 1 ? 's' : ''} filtrado${filteredUsers.length !== 1 ? 's' : ''}`
                  : `Mostrando usuarios ${displayRange.start}-${displayRange.end} de ${filteredUsers.length}`
                }
              </span>
            </div>

            <div className="page-size-selector">
              <span>Mostrar:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
              <span>por página</span>
            </div>

            <div className="simple-pagination">
              <Button
                icon={<LeftOutlined />}
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                Anterior
              </Button>

              <div className="current-page-indicator">
                Página {currentPage}
              </div>

              <Button
                icon={<RightOutlined />}
                onClick={handleNextPage}
                iconPosition="end"
                disabled={currentPage >= totalPages}
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}

        {hasActiveFilters && filteredUsers.length > 0 && (
          <div className="search-clear-section">
            <Button
              type="link"
              onClick={handleClearAllFilters}
              style={{ color: '#0f5197' }}
            >
              Mostrar todos los usuarios
            </Button>
          </div>
        )}
      </div>

      <AddUsers
        open={showAddUserModal}
        onSuccess={handleUserSuccess}
        onCancel={handleModalCancel}
      />
      <AddUsers
        open={showEditUserModal}
        isEditMode={true}
        userData={editingUser}
        onSuccess={handleUserSuccess}
        onCancel={handleModalCancel}
      />
    </div>
  );
}